import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { templateAPI } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import RichTextEditor from '../components/RichTextEditor';
import {
  FileText, Award, ScrollText, Stamp, Bell, Layers,
  ArrowLeft, ArrowRight, Check, Loader2, Eye, Sparkles,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  minutes:     { label: 'Minutes',     desc: 'Meeting minutes documenting proceedings and decisions',           icon: FileText,   color: '#00A3A3' },
  resolution:  { label: 'Resolution',  desc: 'Board or stockholder resolutions with formal RESOLVED clauses',  icon: Award,      color: '#F58A26' },
  certificate: { label: 'Certificate', desc: "Secretary's certificates attesting corporate actions",            icon: Stamp,      color: '#7C3AED' },
  notice:      { label: 'Notice',      desc: 'Meeting notices with date, venue, and agenda information',        icon: Bell,       color: '#2563EB' },
  agenda:      { label: 'Agenda',      desc: 'Meeting agenda outlines with order of business',                  icon: ScrollText, color: '#059669' },
  other:       { label: 'Other',       desc: 'Custom document templates for other governance needs',            icon: Layers,     color: '#6B7280' },
};

const MEETING_TYPES = [
  { value: 'board', label: 'Board' },
  { value: 'committee', label: 'Committee' },
  { value: 'asm', label: 'ASM' },
  { value: 'ssm', label: 'SSM' },
];

const PLACEHOLDERS = {
  'organization.name': 'Organization Name',
  'organization.address': 'Principal Office Address',
  'meeting.title': 'Meeting Title',
  'meeting.date': 'Meeting Date',
  'meeting.time': 'Meeting Time',
  'meeting.venue': 'Meeting Venue',
  'meeting.type': 'Meeting Type',
  'meeting.body_name': 'Body Name',
  'secretary.name': 'Corporate Secretary Name',
  'chairman.name': 'Chairman Name',
  'quorum.value': 'Quorum Value',
  'resolution.number': 'Resolution Number',
  'resolution.text': 'Resolution Text',
  'resolution.title': 'Resolution Title',
  'certificate.number': 'Certificate Number',
  'current_date': 'Current Date',
};

const SAMPLE_DATA = {
  'organization.name': 'ABC Holdings Corporation',
  'organization.address': '25/F Tower One, Ayala Triangle, Makati City 1226',
  'meeting.title': 'Regular Board Meeting \u2014 Q1 2026',
  'meeting.date': 'March 25, 2026',
  'meeting.time': '2:00 PM (PHT)',
  'meeting.venue': 'Board Room, 25/F Tower One, Ayala Triangle, Makati City',
  'meeting.type': 'Board Meeting',
  'meeting.body_name': 'Board of Directors',
  'secretary.name': 'Atty. Maria Clara D. Santos',
  'chairman.name': 'Hon. Jose P. Rizal III',
  'quorum.value': '6 of 11 directors (majority)',
  'resolution.number': 'BR-2026-001',
  'resolution.text': 'RESOLVED, that the Board hereby approves the Annual Budget for FY2026.',
  'resolution.title': 'Approval of Annual Budget FY2026',
  'certificate.number': 'SC-2026-001',
  'current_date': 'March 18, 2026',
};

const STEPS = [
  { num: 1, label: 'Type' },
  { num: 2, label: 'Setup' },
  { num: 3, label: 'Editor' },
  { num: 4, label: 'Review' },
];

const TemplateBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [existingTemplates, setExistingTemplates] = useState([]);

  // Template state
  const [templateType, setTemplateType] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [bodyHtml, setBodyHtml] = useState('');
  const [starterBase, setStarterBase] = useState('scratch');

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    templateAPI.getAll().then(res => setExistingTemplates(res.data)).catch(() => {});
  }, []);

  const renderPreview = useCallback((html) => {
    let rendered = html || '';
    Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
      rendered = rendered.replace(
        new RegExp(`\\{\\{${key.replace('.', '\\.')}\\}\\}`, 'g'),
        `<span style="background:#FEF3C7;padding:0 4px;border-radius:3px;font-weight:600;">${value}</span>`
      );
    });
    setPreviewHtml(rendered);
  }, []);

  useEffect(() => { renderPreview(bodyHtml); }, [bodyHtml, renderPreview]);

  const toggleMeetingType = (val) => {
    setMeetingTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const handleStarterChange = (base) => {
    setStarterBase(base);
    if (base === 'scratch') {
      setBodyHtml('');
    } else {
      const tmpl = existingTemplates.find(t => t.id === base);
      if (tmpl) setBodyHtml(tmpl.body_html || '');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await templateAPI.create({
        name: templateName,
        template_type: templateType,
        applicable_meeting_types: meetingTypes,
        applicable_body_types: [],
        body_html: bodyHtml,
        placeholders: PLACEHOLDERS,
      });
      toast.success('Template created successfully!');
      navigate('/templates');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!templateType;
    if (step === 2) return templateName.trim().length > 0;
    if (step === 3) return bodyHtml.trim().length > 0;
    return true;
  };

  const meta = TYPE_META[templateType];

  // Starters for step 2
  const starters = existingTemplates.filter(t => t.template_type === templateType);

  return (
    <div className="space-y-6" data-testid="template-builder">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/templates')} data-testid="builder-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Template Builder</h1>
          <p className="text-sm text-[#5A6570]">Create a custom document template step by step</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2" data-testid="step-indicator">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.num}>
            {i > 0 && <ChevronRight className="h-4 w-4 text-[#A8B1B9] shrink-0" />}
            <button
              onClick={() => s.num < step && setStep(s.num)}
              disabled={s.num > step}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                s.num === step
                  ? 'text-white shadow-sm'
                  : s.num < step
                  ? 'bg-[#00A3A3]/10 text-[#00A3A3] cursor-pointer hover:bg-[#00A3A3]/20'
                  : 'bg-[#F0F2F4] text-[#A8B1B9]'
              }`}
              style={s.num === step ? { backgroundColor: meta?.color || '#1A1D21' } : {}}
              data-testid={`step-${s.num}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                s.num < step ? 'bg-[#00A3A3] text-white' : s.num === step ? 'bg-white/30 text-white' : 'bg-[#E2E6EA] text-[#A8B1B9]'
              }`}>
                {s.num < step ? <Check className="h-3 w-3" /> : s.num}
              </span>
              {s.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Choose Type */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-1-content">
          <h2 className="text-lg font-semibold text-[#1A1D21]">What type of document template?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(TYPE_META).map(([key, m]) => {
              const Icon = m.icon;
              const selected = templateType === key;
              return (
                <button
                  key={key}
                  onClick={() => setTemplateType(key)}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    selected
                      ? 'shadow-md'
                      : 'border-[#E2E6EA] hover:border-[#A8B1B9]'
                  }`}
                  style={selected ? { borderColor: m.color, backgroundColor: `${m.color}08` } : {}}
                  data-testid={`type-${key}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${m.color}15` }}>
                      <Icon className="h-5 w-5" style={{ color: m.color }} />
                    </div>
                    <span className="font-semibold text-[#1A1D21]">{m.label}</span>
                  </div>
                  <p className="text-xs text-[#5A6570] leading-relaxed">{m.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Name & Setup */}
      {step === 2 && (
        <div className="space-y-6" data-testid="step-2-content">
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label className="font-medium">Template Name</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={`e.g., Custom ${meta?.label || 'Document'} Template`}
                  className="border-[#E2E6EA] text-sm"
                  data-testid="builder-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Applicable Meeting Types</Label>
                <p className="text-xs text-[#5A6570]">Which meeting types can use this template? Leave empty for all.</p>
                <div className="flex flex-wrap gap-2">
                  {MEETING_TYPES.map(mt => {
                    const active = meetingTypes.includes(mt.value);
                    return (
                      <Button
                        key={mt.value}
                        type="button"
                        variant={active ? 'default' : 'outline'}
                        size="sm"
                        className={`text-xs h-8 ${active ? 'text-white' : 'border-[#E2E6EA]'}`}
                        style={active ? { backgroundColor: meta?.color } : {}}
                        onClick={() => toggleMeetingType(mt.value)}
                        data-testid={`meeting-type-${mt.value}`}
                      >
                        {mt.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Starting Point</Label>
                <p className="text-xs text-[#5A6570]">Start from scratch or use an existing template as a base.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <button
                    onClick={() => handleStarterChange('scratch')}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      starterBase === 'scratch' ? 'border-[#00A3A3] bg-[#00A3A3]/5' : 'border-[#E2E6EA] hover:border-[#A8B1B9]'
                    }`}
                    data-testid="starter-scratch"
                  >
                    <span className="font-medium text-sm text-[#1A1D21]">Start from scratch</span>
                    <p className="text-xs text-[#5A6570] mt-1">Build your template from a blank canvas</p>
                  </button>
                  {starters.map(tmpl => (
                    <button
                      key={tmpl.id}
                      onClick={() => handleStarterChange(tmpl.id)}
                      className={`text-left p-4 rounded-lg border-2 transition-all ${
                        starterBase === tmpl.id ? 'border-[#00A3A3] bg-[#00A3A3]/5' : 'border-[#E2E6EA] hover:border-[#A8B1B9]'
                      }`}
                      data-testid={`starter-${tmpl.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-[#1A1D21] truncate">{tmpl.name}</span>
                        {tmpl.scope === 'LANDLORD_DEFAULT' && (
                          <Badge variant="outline" className="text-[9px] py-0 shrink-0">Default</Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#5A6570] mt-1 line-clamp-1">
                        {tmpl.body_html ? tmpl.body_html.replace(/<[^>]*>/g, '').substring(0, 60) + '...' : 'No content'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Editor with Live Preview */}
      {step === 3 && (
        <div className="space-y-4" data-testid="step-3-content">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#1A1D21]">
              Edit Template Content
            </h2>
            <div className="flex items-center gap-2 text-xs text-[#5A6570]">
              <Sparkles className="h-3.5 w-3.5 text-[#F58A26]" />
              Use the placeholder button to insert dynamic fields
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Editor */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-[#00A3A3]" />
                <span className="text-xs font-medium text-[#5A6570] uppercase tracking-wider">Editor</span>
              </div>
              <RichTextEditor
                content={bodyHtml}
                onChange={setBodyHtml}
                placeholders={PLACEHOLDERS}
                placeholder="Start building your template..."
              />
            </div>

            {/* Live Preview */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="h-3 w-3 text-[#F58A26]" />
                <span className="text-xs font-medium text-[#5A6570] uppercase tracking-wider">Live Preview</span>
                <Badge className="text-[9px] py-0 bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]">Sample Data</Badge>
              </div>
              <div
                className="border border-[#E2E6EA] rounded-lg bg-white p-6 min-h-[340px] overflow-auto shadow-inner"
                data-testid="builder-live-preview"
              >
                {previewHtml ? (
                  <div
                    className="prose prose-sm max-w-none font-serif"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Eye className="h-8 w-8 text-[#E2E6EA] mb-3" />
                    <p className="text-sm text-[#A8B1B9]">Start typing to see your template preview here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review & Save */}
      {step === 4 && (
        <div className="space-y-4" data-testid="step-4-content">
          <h2 className="text-lg font-semibold text-[#1A1D21]">Review Your Template</h2>

          {/* Summary */}
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-5">
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-[#A8B1B9] text-xs uppercase tracking-wider">Name</span>
                  <p className="font-medium text-[#1A1D21] mt-0.5" data-testid="review-name">{templateName}</p>
                </div>
                <div>
                  <span className="text-[#A8B1B9] text-xs uppercase tracking-wider">Type</span>
                  <p className="font-medium text-[#1A1D21] mt-0.5 capitalize" data-testid="review-type">{meta?.label}</p>
                </div>
                <div>
                  <span className="text-[#A8B1B9] text-xs uppercase tracking-wider">Meeting Types</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {meetingTypes.length > 0
                      ? meetingTypes.map(mt => (
                          <Badge key={mt} variant="outline" className="text-[10px] uppercase">{mt}</Badge>
                        ))
                      : <span className="text-[#5A6570] text-xs">All types</span>
                    }
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Full Preview */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#F58A26]" />
              <span className="text-sm font-medium text-[#5A6570]">Preview with sample Philippine corporate data</span>
            </div>
            <div
              className="border border-[#E2E6EA] rounded-lg bg-white p-8 shadow-inner"
              data-testid="review-preview"
            >
              <div
                className="prose prose-sm max-w-none font-serif"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-[#E2E6EA]">
        <Button
          variant="outline"
          onClick={() => step === 1 ? navigate('/templates') : setStep(step - 1)}
          className="border-[#E2E6EA]"
          data-testid="builder-prev"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>

        {step < 4 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="text-white"
            style={{ backgroundColor: meta?.color || '#00A3A3' }}
            data-testid="builder-next"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00A3A3] hover:bg-[#008585] text-white"
            data-testid="builder-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Create Template
          </Button>
        )}
      </div>
    </div>
  );
};

export default TemplateBuilder;
